/**
 * Cart State Manager
 * Manages product card states based on cart contents
 */

class CartStateManager {
  constructor() {
    this.cartItems = [];
    this.init();
  }

  init() {
    // Load initial cart state and update product cards
    this.loadCartState();
    
    // Listen for cart updates to refresh product card states
    window.addEventListener('cartcount:updated', (event) => {
      if (event.detail && event.detail.cart) {
        this.updateCartItems(event.detail.cart.items);
        this.updateAllProductCards();
      }
    });

    // Listen for theme's cart events
    window.addEventListener('cartcount:update', (event) => {
      if (event.detail && event.detail.items) {
        this.updateCartItems(event.detail.items);
        this.updateAllProductCards();
      }
    });
  }

  /**
   * Load initial cart state from server
   */
  async loadCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.updateCartItems(cart.items);
      this.updateAllProductCards();
      
    } catch (error) {
      console.error('Failed to load cart state:', error);
    }
  }

  /**
   * Update internal cart items array
   */
  updateCartItems(items) {
    this.cartItems = items || [];
  }

  /**
   * Check if a variant is in the cart
   */
  isVariantInCart(variantId) {
    return this.cartItems.some(item => item.variant_id.toString() === variantId.toString());
  }

  /**
   * Update all product cards on the page
   */
  updateAllProductCards() {
    // Find all product cards with variant IDs
    const productCards = document.querySelectorAll('[data-variant-id], [wizzy-data-variant-id]');
    
    console.log(`Updating ${productCards.length} product cards based on cart state`);
    
    productCards.forEach(card => {
      this.updateProductCard(card);
    });
  }

  /**
   * Update a single product card based on cart state
   */
  updateProductCard(cardElement) {
    const isWizzy = cardElement.hasAttribute('wizzy-data-variant-id');
    const variantId = isWizzy ? 
      cardElement.getAttribute('wizzy-data-variant-id') : 
      cardElement.getAttribute('data-variant-id');
    
    if (!variantId) return;

    const isInCart = this.isVariantInCart(variantId);
    
    if (isInCart) {
      this.convertToGoToCart(cardElement, isWizzy);
    } else {
      this.convertToAddToCart(cardElement, isWizzy);
    }
  }

  /**
   * Convert product card button to "Go to Cart"
   */
  convertToGoToCart(element, isWizzy = false) {
    // Skip if already converted
    if (element.classList.contains('go-to-cart-class')) return;

    const hiddenClass = isWizzy ? "hidden-wizzy-atc" : "hidden-atc";
    const baseClass = isWizzy ? "go-to-cart-class wizzy-data-variant-id color-hai" : "go-to-cart-class data-variant-id";
    
    // Hide original button
    element.classList.add(hiddenClass);
    element.style.display = 'none';
    
    // Remove any existing go-to-cart buttons
    const existingGoToCart = element.parentNode.querySelector('.go-to-cart-class');
    if (existingGoToCart) {
      existingGoToCart.remove();
    }

    // Create go-to-cart button
    const goToCartButton = this.createGoToCartButton(baseClass);
    
    // Insert after the original button
    element.parentNode.insertBefore(goToCartButton, element.nextSibling);
  }

  /**
   * Convert product card button back to "Add to Cart"
   */
  convertToAddToCart(element, isWizzy = false) {
    const hiddenClass = isWizzy ? "hidden-wizzy-atc" : "hidden-atc";
    
    // Show original button
    element.classList.remove(hiddenClass, 'go-to-cart-class');
    element.style.display = '';
    
    // Remove go-to-cart button
    const goToCartButton = element.parentNode.querySelector('.go-to-cart-class');
    if (goToCartButton) {
      goToCartButton.remove();
    }
  }

  /**
   * Create a "Go to Cart" button element
   */
  createGoToCartButton(className) {
    const button = document.createElement('button');
    button.className = className;
    button.onclick = function() {
      const myLink = document.getElementById('myLink');
      if (myLink) {
        myLink.click();
      } else {
        window.location.href = '/cart';
      }
    };
    
    button.innerHTML = `
      <a href="javascript:void(0)" class="cart_success">
        Go to cart
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
          <g id="Complete">
            <g id="arrow-right">
              <g>
                <polyline data-name="Right" fill="none" id="Right-2" points="16.4 7 21.5 12 16.4 17" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="3"></polyline>
                <line fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" x1="2.5" x2="19.2" y1="12" y2="12"></line>
              </g>
            </g>
          </g>
        </svg>
      </a>
    `;
    
    return button;
  }

  /**
   * Refresh all product cards (public method)
   */
  refresh() {
    this.loadCartState();
  }
}

// Initialize the cart state manager
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for other scripts to load
  setTimeout(() => {
    window.cartStateManager = new CartStateManager();
  }, 500);
  
  // Also trigger on page visibility change (for SPA-like behavior)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && window.cartStateManager) {
      setTimeout(() => {
        window.cartStateManager.refresh();
      }, 200);
    }
  });
  
  // Trigger when navigating back to page
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && window.cartStateManager) {
      setTimeout(() => {
        window.cartStateManager.refresh();
      }, 200);
    }
  });
});

// Global helper function
window.updateProductCardStates = function() {
  window.cartStateManager?.refresh();
};